import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  const [language, setLanguage] = useState('af'); // 'af' for Afrikaans, 'en' for English

  const toggleLanguage = () => {
    setLanguage(language === 'af' ? 'en' : 'af');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-orange-600 hover:text-orange-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'af' ? 'Terug na Tuisblad' : 'Back to Home'}
          </Link>
          
          <Button
            onClick={toggleLanguage}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            size="sm"
          >
            <Globe className="h-4 w-4 mr-2" />
            {language === 'af' ? 'English' : 'Afrikaans'}
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {language === 'af' ? <AfrikaansContent /> : <EnglishContent />}
        </div>
      </div>
    </div>
  );
};

const AfrikaansContent = () => (
  <>
    <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
      Privaatheidsbeleid
    </h1>
    <p className="text-sm text-gray-600 mb-8">Laas Opgedateer: 21 April 2026</p>

    <div className="space-y-6 text-gray-700">
      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Inleiding</h2>
        <p>
          Welkom by DIE BIBLIOTEEK. Ons respekteer jou privaatheid en verbind onsself tot die beskerming van jou persoonlike inligting. 
          Hierdie Privaatheidsbeleid verduidelik hoe ons jou inligting versamel, gebruik, en beskerm wanneer jy ons platform gebruik.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Inligting wat Ons Versamel</h2>
        <p className="mb-2">Ons versamel die volgende tipes inligting:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li><strong>Persoonlike Inligting:</strong> Naam, van, e-posadres, geboortedatum</li>
          <li><strong>Rekeninginligting:</strong> Banknaam, rekeningnommer, rekeningtipe, takkode (vir proeflees betalings)</li>
          <li><strong>Boek Inligting:</strong> Boeke wat jy oplaai, metadata, dekblaaie</li>
          <li><strong>Gebruik Data:</strong> Hoe jy die platform gebruik, leer, en navigeer</li>
          <li><strong>Betaling Inligting:</strong> Betalingstransaksies verwerk deur EFT</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Hoe Ons Jou Inligting Gebruik</h2>
        <p className="mb-2">Ons gebruik jou inligting vir:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Registrasie en rekeninge bestuur</li>
          <li>Boek indiening, proeflees, en publikasie prosesse</li>
          <li>Betalings aan proeflees vir voltooide werk</li>
          <li>Kommunikasie oor jou rekeninge en diens opdaterings</li>
          <li>Verbetering van ons platform en dienste</li>
          <li>Nakoming van wetlike verpligtinge</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Betaling Verwerking</h2>
        <p>
          Betalings word verwerk deur derde party betalingsverskaffers (die administrateur). Ons stoor nie jou volledige 
          betaling kaart besonderhede nie. Jou bankbesonderhede word slegs gebruik vir die doel van uitbetalings aan 
          goedgekeurde proeflees. Alle betalingstransaksies word beveilig volgens industrie standaarde verwerk.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Beveiliging</h2>
        <p>
          Ons implementeer verskeie sekuriteitsmaatreëls om jou persoonlike inligting te beskerm, insluitend:
        </p>
        <ul className="list-disc ml-6 space-y-2 mt-2">
          <li>Geënkripteerde data berging</li>
          <li>Veilige HTTPS verbindings</li>
          <li>Toegang beheer en verifikasie</li>
          <li>Gereelde sekuriteit oudits</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Deel van Inligting</h2>
        <p className="mb-2">Ons deel jou inligting nie met derde partye nie, behalwe:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Met jou toestemming</li>
          <li>Vir betaling verwerking (EFT)</li>
          <li>Om te voldoen aan wetlike verpligtinge</li>
          <li>Om ons regte, eiendom, of veiligheid te beskerm</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Intellektuele Eiendom</h2>
        <p>
          Alle boeke wat op die platform gelaai word, bly die intellektuele eiendom van die oorspronklike skrywers. 
          Proeflees verbind hulself tot vertroulikheid en sal nie boeke deel, kopieer, of publiseer sonder toestemming nie.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Jou Regte</h2>
        <p className="mb-2">Jy het die reg om:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Toegang tot jou persoonlike inligting te versoek</li>
          <li>Korreksies aan jou inligting te versoek</li>
          <li>Verwydering van jou inligting te versoek</li>
          <li>Beswaar te maak teen die verwerking van jou inligting</li>
          <li>Data oordraagbaarheid te versoek</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Koekies</h2>
        <p>
          Ons gebruik koekies en soortgelyke tegnologieë om jou ervaring op ons platform te verbeter, jou voorkeure 
          te onthou, en te verstaan hoe jy ons diens gebruik. Jy kan koekies in jou blaaier instellings afslaan, 
          maar dit kan die funksionaliteit van die platform beïnvloed.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Ouderdomsbeperking en Verifikasie</h2>
        <p className="mb-3">
          Deur toegang tot of gebruik van Die Biblioteek te verkry, bevestig jy dat jy 18 jaar of ouer is.
        </p>
        <p className="mb-3">
          Die platform is streng bedoel vir volwasse gebruikers, en ons versamel of verwerk nie doelbewus persoonlike 
          inligting van persone onder die ouderdom van 18 jaar nie.
        </p>
        <p className="mb-3">
          As deel van ons registrasieproses mag gebruikers versoek word om hul ouderdom te verifieer. Deur voort te gaan 
          met die gebruik van die platform, waarborg jy dat alle inligting wat verskaf is, akkuraat en waar is.
        </p>
        <p>
          Indien ons bewus raak daarvan dat 'n gebruiker jonger as 18 jaar is, behou ons die reg voor om toegang tot die 
          platform op te skort of te beeindig en enige verwante data te verwyder.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Wysigings aan Hierdie Beleid</h2>
        <p>
          Ons kan hierdie Privaatheidsbeleid van tyd tot tyd opdateer. Ons sal jou in kennis stel van enige wesenlike 
          veranderinge deur 'n kennisgewing op ons platform te plaas of deur 'n e-pos aan jou te stuur.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Kontak Ons</h2>
        <p>
          As jy enige vrae oor hierdie Privaatheidsbeleid het, kontak ons asseblief by:
        </p>
        <div className="mt-3 p-4 bg-orange-50 rounded-lg">
          <p><strong>DIE BIBLIOTEEK</strong></p>
          <p>E-pos: Leesinafrikaans@diebiblioteek.co.za</p>
          <p>Website: https://diebiblioteek.co.za</p>
        </div>
      </section>

      <section className="border-t pt-6 mt-8">
        <p className="text-sm text-gray-600">
          Deur die gebruik van DIE BIBLIOTEEK platform, stem jy in tot die terme van hierdie Privaatheidsbeleid. 
          As jy nie saamstem nie, moet jy asseblief nie ons diens gebruik nie.
        </p>
      </section>
    </div>
  </>
);

const EnglishContent = () => (
  <>
    <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
      Privacy Policy
    </h1>
    <p className="text-sm text-gray-600 mb-8">Last Updated: April 21, 2026</p>

    <div className="space-y-6 text-gray-700">
      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
        <p>
          Welcome to DIE BIBLIOTEEK. We respect your privacy and are committed to protecting your personal information. 
          This Privacy Policy explains how we collect, use, and protect your information when you use our platform.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
        <p className="mb-2">We collect the following types of information:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li><strong>Personal Information:</strong> First name, last name, email address, date of birth</li>
          <li><strong>Account Information:</strong> Bank name, account number, account type, branch code (for proofreader payments)</li>
          <li><strong>Book Information:</strong> Books you upload, metadata, cover images</li>
          <li><strong>Usage Data:</strong> How you use, access, and navigate the platform</li>
          <li><strong>Payment Information:</strong> Payment transactions processed through EFT</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
        <p className="mb-2">We use your information for:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Registration and account management</li>
          <li>Book submission, proofreading, and publication processes</li>
          <li>Payments to proofreaders for completed work</li>
          <li>Communication about your account and service updates</li>
          <li>Improvement of our platform and services</li>
          <li>Compliance with legal obligations</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Payment Processing</h2>
        <p>
          Payments are processed by third-party payment providers (the administrator). We do not store your complete 
          payment card details. Your banking details are only used for the purpose of payouts to approved 
          proofreaders. All payment transactions are processed securely according to industry standards.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Security</h2>
        <p>
          We implement various security measures to protect your personal information, including:
        </p>
        <ul className="list-disc ml-6 space-y-2 mt-2">
          <li>Encrypted data storage</li>
          <li>Secure HTTPS connections</li>
          <li>Access control and verification</li>
          <li>Regular security audits</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Information Sharing</h2>
        <p className="mb-2">We do not share your information with third parties except:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>With your consent</li>
          <li>For payment processing (EFT)</li>
          <li>To comply with legal obligations</li>
          <li>To protect our rights, property, or safety</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Intellectual Property</h2>
        <p>
          All books uploaded to the platform remain the intellectual property of the original writers. 
          Proofreaders commit to confidentiality and will not share, copy, or publish books without permission.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Your Rights</h2>
        <p className="mb-2">You have the right to:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Request access to your personal information</li>
          <li>Request corrections to your information</li>
          <li>Request deletion of your information</li>
          <li>Object to the processing of your information</li>
          <li>Request data portability</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Cookies</h2>
        <p>
          We use cookies and similar technologies to improve your experience on our platform, remember your preferences, 
          and understand how you use our service. You can disable cookies in your browser settings, 
          but this may affect the functionality of the platform.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Age Restriction & Verification</h2>
        <p className="mb-3">
          By accessing or using Die Biblioteek platform, you confirm that you are 18 years of age or older.
        </p>
        <p className="mb-3">
          The platform is strictly intended for adult users, and we do not knowingly collect or process personal 
          information from individuals under the age of 18.
        </p>
        <p className="mb-3">
          As part of our onboarding process, users may be required to verify their age. By continuing to use the 
          platform, you warrant that all information provided is accurate and truthful.
        </p>
        <p>
          If we become aware that a user is under the age of 18, we reserve the right to suspend or terminate access 
          to the platform and delete any associated data.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any significant 
          changes by posting a notice on our platform or by sending you an email.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact us at:
        </p>
        <div className="mt-3 p-4 bg-orange-50 rounded-lg">
          <p><strong>DIE BIBLIOTEEK</strong></p>
          <p>Email: Leesinafrikaans@diebiblioteek.co.za</p>
          <p>Website: https://diebiblioteek.co.za</p>
        </div>
      </section>

      <section className="border-t pt-6 mt-8">
        <p className="text-sm text-gray-600">
          By using the DIE BIBLIOTEEK platform, you agree to the terms of this Privacy Policy. 
          If you do not agree, please do not use our service.
        </p>
      </section>
    </div>
  </>
);

export default PrivacyPolicy;
